import * as React from 'react';
import { Fragment, Component } from 'react';
import { observer } from 'mobx-react';
import { inject } from '../utils';
import { withTheme } from 'styled-components';
import { styled } from '../utils/styled';
import { propStyles } from '../utils/propStyles';
import Translate from './Translate';
import Icon from './Icon';
import { computed, observable, action } from 'mobx';
import { ReadMoreLessOptions } from '../models/configuration/components/ReadMoreLessOptions';
import { AccessibilityStore } from '../stores';
import { utils } from '@kurtosys/ksys-app-template';
import { KEYBOARD_KEYS } from '../constants';
import ReadMoreLessWrapper from './ReadMoreLessWrapper';
import { Theme } from '../models/Theme';

const ReadMoreLessButton = styled<any, any>(
    'button',
    'readMoreLessButton',
    'readMoreLess'
)`
    ${propStyles};
`;

ReadMoreLessButton.defaultProps = {
    display: 'flex',
    alignItems: 'center',
    fontFamily: 'body',
    fontWeight: 'normal',
    fontSize: 'sm',
    color: 'gray500',
    bgColor: 'transparent',
    borderStyle: 'none',
    cursor: 'pointer',
    outline: 'none',
    selected: {
        color: 'body',
    },
    padding: '5px 0',
};

const iconProps = {
    height: '1.4em',
    width: '1.4em',
    margin: '0 0 0 0.25em',
};

const ACCEPT_KEYS = [
    KEYBOARD_KEYS.ENTER,
    KEYBOARD_KEYS.SPACE,
    KEYBOARD_KEYS.SPACEBAR,
];

interface ReadMoreLessProps {
    theme?: Theme;
    height?: string;
    readMoreLessOptions?: ReadMoreLessOptions;
    accessibilityStore?: AccessibilityStore;
    readMoreCommentary?: React.ReactNode;
}

@inject('accessibilityStore')
@observer
class ReadMoreLess extends Component<ReadMoreLessProps> {
    readMoreLessWrapperRef: HTMLElement;
    @observable expanded = false;

    saveWrapperReference(instance: HTMLElement) {
        this.readMoreLessWrapperRef = instance;
    }

    @action
    toggleReadMore = () => {
        this.expanded = !this.expanded;
    };

    @action
    onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
        const key = e.key;
        if (ACCEPT_KEYS.indexOf(key as KEYBOARD_KEYS) !== -1) {
            e.preventDefault();
            e.stopPropagation();
            this.expanded = !this.expanded;
        }
    };

    @computed
    get overflow() {
        return this.expanded ? 'visible' : 'hidden';
    }

    @computed
    get height() {
        return this.expanded ? 'auto' : this.props.readMoreLessOptions.height;
    }

    @computed
    get readMoreText() {
        return (
            this.props.readMoreLessOptions &&
            this.props.readMoreLessOptions.readMoreText
        );
    }

    @computed
    get readLessText() {
        return (
            this.props.readMoreLessOptions &&
            this.props.readMoreLessOptions.readLessText
        );
    }

    @computed
    get text() {
        return this.expanded ? this.readLessText : this.readMoreText;
    }

    @computed
    get align() {
        const align =
            (this.props.readMoreLessOptions &&
                this.props.readMoreLessOptions.align) ||
            'right';
        if (align === 'left') {
            return 'auto 0 0 0';
        }

        if (align === 'center') {
            return '0 auto';
        }

        if (align === 'right') {
            return '0 0 0 auto';
        }
    }

    @computed
    get alignIcon() {
        return (
            (this.props.readMoreLessOptions &&
                this.props.readMoreLessOptions.alignIcon) ||
            'right'
        );
    }

    @computed
    get iconFilename() {
        if (
            this.props.readMoreLessOptions &&
            (this.props.readMoreLessOptions.readMoreIcon ||
                this.props.readMoreLessOptions.readLessIcon)
        ) {
            return this.expanded
                ? this.props.readMoreLessOptions.readLessIcon
                : this.props.readMoreLessOptions.readMoreIcon;
        }
    }

    @computed
    get hasReadMoreCommentary(): boolean {
        const { readMoreCommentary } = this.props;
        return !utils.typeChecks.isNullOrEmpty(readMoreCommentary);
    }

    render() {
        const {
            theme,
            children,
            readMoreCommentary,
            accessibilityStore,
        } = this.props;
        const {
            toggleReadMore,
            onKeyDown,
            align,
            alignIcon,
            iconFilename,
            text,
        } = this;

        const icon = (
            <Icon
                fileName={iconFilename}
                {...iconProps}
                {...theme.ReadMoreLessIcon}
            />
        );

        return (
            <Fragment>
                <ReadMoreLessWrapper
                    height={
                        this.hasReadMoreCommentary ? 'initial' : this.height
                    }
                    overflow={
                        this.hasReadMoreCommentary ? 'initial' : this.overflow
                    }
                >
                    {children}
                </ReadMoreLessWrapper>
                {this.expanded && this.hasReadMoreCommentary && (
                    <ReadMoreLessWrapper
                        height={'initial'}
                        overflow={'initial'}
                        tabIndex={0}
                        isFocused={this.expanded}
                        getReadMoreLessWrapperReference={(
                            instance: HTMLElement
                        ) => {
                            this.saveWrapperReference(instance);
                        }}
                    >
                        {readMoreCommentary}
                    </ReadMoreLessWrapper>
                )}
                <ReadMoreLessButton
                    aria-label={accessibilityStore.buttonAccessibilityValue(
                        'ReadMoreLessButton'
                    )}
                    onClick={toggleReadMore}
                    onKeyDown={e => onKeyDown(e)}
                    data-metric-id={`commentary-readMore-toggle`}
                    margin={align}
                    {...theme.ReadMoreLessButton}
                >
                    {iconFilename && alignIcon === 'left' && (
                        <Fragment>{icon}</Fragment>
                    )}
                    {text && <Translate>{text}</Translate>}
                    {iconFilename && alignIcon === 'right' && (
                        <Fragment>{icon}</Fragment>
                    )}
                </ReadMoreLessButton>
            </Fragment>
        );
    }
}

export default withTheme(ReadMoreLess);



// WEBPACK FOOTER //
// ./src/Components/ReadMoreLess.tsx